import React from "react";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import SeoHead from "@/components/SeoHead";
import PageShell from "@/components/PageShell";
import PageBanner from "@/components/PageBanner";
import { Coffee, Wrench, Users, Sparkles, ArrowRight } from "lucide-react";

const BANNER = "https://images.unsplash.com/photo-1511920170033-f8396924c348?auto=format&fit=crop&w=2000&q=80";
const SPLIT_IMG = "/miscele-espresso.jpg";
const LINEUP_IMG = "/miscele-lineup.jpg";

const SERVICES = [
  { icon: Sparkles, key: "custom" },
  { icon: Wrench, key: "equipment" },
  { icon: Coffee, key: "support" },
  { icon: Users, key: "consulting" },
];

const LeMiscele = () => {
  const { t } = useTranslation();
  return (
    <PageShell>
      <SeoHead pageKey="miscele" image={BANNER} />
      <PageBanner
        eyebrow={t("miscele.banner.eyebrow")}
        title={t("miscele.banner.title")}
        subtitle={t("miscele.banner.subtitle")}
        image={BANNER}
      />

      <section className="section-y" style={{ background: "#fbf5e9" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="divider-beans font-serif-display uppercase tracking-[0.3em] text-xs mb-5" style={{ justifyContent: "flex-start" }}>
              <span />{t("miscele.eyebrow")}<span />
            </div>
            <h2 className="font-serif-display font-bold leading-tight" style={{ color: "#2a160c", fontSize: "clamp(1.9rem, 3vw, 2.6rem)" }}>
              {t("miscele.title")}
            </h2>
            <div className="mt-6 space-y-4 text-[15px] sm:text-base leading-relaxed" style={{ color: "#3b2417" }}>
              <p>{t("miscele.p1")}</p>
              <p dangerouslySetInnerHTML={{ __html: t("miscele.p2") }} />
              <p dangerouslySetInnerHTML={{ __html: t("miscele.p3") }} />
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(91,54,33,0.15)", boxShadow: "0 20px 40px -25px rgba(42,22,12,0.35)" }}>
              <img
                src={LINEUP_IMG}
                alt={t("miscele.lineupAlt", "Caffè Cola — La Special, Decaffeinato, Superbar e il barattolo La Special da 250 g")}
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
          </div>

          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img src={SPLIT_IMG} alt={t("miscele.splitAlt")} className="w-full h-[520px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 rounded-2xl px-5 py-4 shadow-xl max-w-[240px]" style={{ background: "#2a160c", color: "#f5ead6" }}>
              <p className="font-serif-display text-xl font-bold" style={{ color: "#c5371f" }}>{t("miscele.badgeValue")}</p>
              <p className="text-xs uppercase tracking-wider opacity-90">{t("miscele.badgeYears")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y" style={{ background: "#f5ead6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-serif-display uppercase tracking-[0.3em] text-xs" style={{ color: "#c5371f" }}>{t("miscele.servicesEyebrow")}</span>
            <h2 className="font-serif-display font-bold mt-3" style={{ color: "#2a160c", fontSize: "clamp(1.9rem, 3.2vw, 2.8rem)" }}>
              {t("miscele.servicesTitle")}
            </h2>
            <p className="mt-4 max-w-2xl mx-auto" style={{ color: "#3b2417" }} dangerouslySetInnerHTML={{ __html: t("miscele.servicesIntro") }} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map(({ icon: Icon, key }) => (
              <div
                key={key}
                className="rounded-2xl p-6 transition hover:-translate-y-1"
                style={{ background: "#fbf5e9", border: "1px solid rgba(91,54,33,0.12)", boxShadow: "0 20px 40px -25px rgba(42,22,12,0.35)" }}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: "#c5371f" }}>
                  <Icon size={22} color="#fff" />
                </div>
                <h3 className="font-serif-display text-lg font-bold" style={{ color: "#2a160c" }}>{t(`miscele.services.${key}.title`)}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "#5b3621" }}>{t(`miscele.services.${key}.desc`)}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/#contatti" className="btn-red focus-ring inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold uppercase tracking-wider text-sm">
              {t("miscele.cta")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default LeMiscele;
